import type { SegmentConditionGroups } from '@reach/shared-types/conditions';
import React, { useEffect, useRef, useState } from 'react';
import type { AvailableFieldsResponseWithTypes } from '../../engage-segments-tab/segment-definitions-api';
import { ConditionGroupList } from './ConditionGroupList';
import { builderStateToConditionGroups, conditionGroupsToBuilderState } from './segment-builder.utils';
import type { BuilderState } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

function generateId(): string {
  return crypto.randomUUID();
}

interface SegmentVisualBuilderProps {
  conditionGroups: SegmentConditionGroups | null;
  onChange: (groups: SegmentConditionGroups) => void;
  availableFields: AvailableFieldsResponseWithTypes;
  isLoading?: boolean;
  onUnsupported: (reason: string) => void;
}

/**
 * Visual segment builder. Converts condition groups JSON to/from internal builder state.
 * State is initialized from conditionGroups when available fields load; subsequent
 * prop changes do not overwrite local state (parent should key this component when
 * switching modes so it remounts with fresh initial state).
 */
export const SegmentVisualBuilder: React.FC<SegmentVisualBuilderProps> = ({
  conditionGroups,
  onChange,
  availableFields,
  isLoading = false,
  onUnsupported,
}) => {
  const [builderState, setBuilderState] = useState<BuilderState>(() => ({
    groups: [],
  }));
  const lastEmittedGroupsRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || !availableFields) return;
    const groups = conditionGroups ?? [];
    const groupsKey = JSON.stringify(groups);
    if (groupsKey === lastEmittedGroupsRef.current) return;
    lastEmittedGroupsRef.current = groupsKey;
    const conversionResult = conditionGroupsToBuilderState(groups, availableFields);
    if (conversionResult.kind === 'unsupported') {
      onUnsupported(conversionResult.reason);
      return;
    }
    const state = conversionResult.state;
    if (state.groups.length === 0) {
      state.groups = [
        {
          id: generateId(),
          logic: 'and',
          conditions: [],
        },
      ];
    }
    setBuilderState(state);
  }, [availableFields, conditionGroups, isLoading, onUnsupported]);

  const handleUpdate = (nextState: BuilderState) => {
    setBuilderState(nextState);
    const groups = builderStateToConditionGroups(nextState);
    lastEmittedGroupsRef.current = JSON.stringify(groups);
    onChange(groups);
  };

  if (isLoading || !availableFields) {
    return (
      <div className={styles.emptyBuilder}>
        <p>Loading fields...</p>
      </div>
    );
  }

  const { contactFields, relatedSchemas } = availableFields;

  if (builderState.groups.length === 0) {
    return (
      <div className={styles.emptyBuilder}>
        <p>No condition groups. Add a group to get started.</p>
      </div>
    );
  }

  return (
    <ConditionGroupList
      state={builderState}
      contactFields={contactFields}
      relatedSchemas={relatedSchemas}
      onUpdate={handleUpdate}
      disabled={isLoading}
    />
  );
};
